import api from './api';
import type { Info360Indicadores, Info360Nota } from '../pages/clientes/info360/types';
import { normalizeCuitForStorage } from '../utils/formatters';

interface VendedorInfo {
    nroVendedor: number;
    nombreVendedor?: string | null;
    telefono?: string | null;
    email?: string | null;
    direccion?: string | null;
}

interface CiudadInfo {
    codigoCiudad: number;
    nombreCiudad?: string | null;
    codigoProvincia?: number | null;
    codigoPostal?: string | null;
    nombreProvincia?: string | null;
}

interface ListaPrecioInfo {
    codigoLista: string;
    descripcion?: string | null;
}

export interface Cliente {
    nroCliente: number;
    nombre: string;
    razonSocial?: string;
    direccion?: string;
    codigoCiudad?: number;
    telefono1?: string;
    telefono2?: string;
    fax?: string;
    email?: string;
    cuit?: string;
    dni?: string | number;
    tipoIva?: string;
    condVta?: string;
    credito?: number;
    nroLista?: string;
    contacto?: string;
    notas?: string;
    fechaAlta?: string;
    fechaBaja?: string;
    tipoCliente?: string;
    ctaMaestra?: number;
    nroCuenta?: number;
    deposito?: number;
    nroClienteExterno?: string;
    vendedor?: number;
    nroCobrador?: number;
    vtoComprob?: number | null;
    reDespacho?: number | null;
    porcDesc?: number;
    porcDesc2?: number;
    porcDesc3?: number;
    atributo1?: string;
    atributo2?: string;
    atributo3?: string;
    atributo4?: string;
    atributo5?: string;
    nroExpreso?: number | string | null;
    codigoDireccion?: string | null;
    codigoDireccionEnvio?: string | null;
    codigoDireccionResumen?: string | null;
    direccionEnvio?: string | null;
    codigoCiudadEnvio?: number | null;
    direccionResumen?: string | null;
    codigoCiudadResumen?: number | null;
    codigoRuta?: number | null;
    vendedorInfo?: VendedorInfo;
    ciudad?: CiudadInfo;
    listaPrecio?: ListaPrecioInfo;
    cobradorInfo?: VendedorInfo;
    cuentaContable?: {
        nroCuenta: number;
        nombreCuenta?: string;
    };
    depositoInfo?: {
        nroUbicacion: number;
        nombreUbicacion?: string;
        direccion?: string;
    };
    transporte?: {
        nroExpreso?: number | string;
        nombre?: string;
        nombreExpreso?: string;
    };
}

interface ClienteSearchParams {
    searchTerm?: string;
    nroVendedor?: number;
    pageNumber: number;
    pageSize: number;
    /** Excluye clientes inactivos (TipoCliente I) del listado (flujos operativos). */
    excluirInactivos?: boolean;
}

export interface GetClienteOptions {
    /** Rechaza clientes inactivos con HTTP 409 (flujos operativos). */
    soloOperativo?: boolean;
}

/** Parámetros para búsquedas en ventas, cobranzas y flujos operativos. */
export const PARAMS_CLIENTE_OPERATIVO = { excluirInactivos: true } as const;

/** Parámetros para informes y consultas que incluyen clientes inactivos. */
export const PARAMS_CLIENTE_TODOS = {} as const;

export const OPTIONS_CLIENTE_OPERATIVO: GetClienteOptions = { soloOperativo: true };

/** Respuesta de consulta al padrón AFIP (precarga; no crea el cliente). */
export interface PadronAfipCliente {
    cuit?: string | null;
    nombre?: string | null;
    razonSocial?: string | null;
    direccion?: string | null;
    localidad?: string | null;
    codigoPostal?: string | null;
    provincia?: string | null;
    tipoIva?: string | null;
    tipoIvaDescripcion?: string | null;
    condicionIvaAfip?: string | null;
    tipoPersona?: string | null;
    estadoClave?: string | null;
    mensaje?: string | null;
}

/** Mensaje de error cuando la API responde 409 por cliente inactivo. */
export const getMensajeClienteNoOperativo = (err: unknown, nroCliente?: number): string => {
    const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
    if (data?.message) return data.message;
    if (nroCliente != null) {
        return `El cliente #${nroCliente} está inactivo y no puede utilizarse en operaciones.`;
    }
    return 'El cliente está inactivo y no puede utilizarse en operaciones.';
};

export const esErrorClienteNoOperativo = (err: unknown): boolean =>
    (err as { response?: { status?: number } })?.response?.status === 409;

interface ClientePagedResult {
    items: Cliente[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface ClienteExportItem {
    nroCliente: number;
    nombre: string;
    codigoDireccion?: string | null;
    razonSocial?: string | null;
    direccion?: string | null;
    codigoCiudad?: number | null;
    telefono1?: string | null;
    telefono2?: string | null;
    email?: string | null;
    cuit?: string | null;
    tipoIva?: string | null;
    condVta?: string | null;
    credito?: number | null;
    nroLista?: string | null;
    contacto?: string | null;
    fechaAlta?: string | null;
    fechaBaja?: string | null;
    tipoCliente?: string | null;
    ctaMaestra?: number | null;
    nroCuenta?: number | null;
    deposito?: number;
    nroClienteExterno?: string | null;
    vendedor?: number | null;
    codigoRuta?: number | null;
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
}

const clienteService = {
    getClientes: async (params: ClienteSearchParams): Promise<ClientePagedResult> => {
        const response = await api.get<ClientePagedResult>('/api/Clientes', { params });
        return response.data;
    },

    getClientesExportData: async (searchTerm?: string): Promise<ClienteExportItem[]> => {
        const response = await api.get<ClienteExportItem[]>('/api/Clientes/export-data', {
            params: searchTerm ? { searchTerm } : undefined,
        });
        return response.data;
    },

    getCliente: async (nroCliente: number, options?: GetClienteOptions): Promise<Cliente> => {
        const response = await api.get<Cliente>(`/api/Clientes/${nroCliente}`, {
            params: options?.soloOperativo ? { soloOperativo: true } : undefined,
        });
        return response.data;
    },

    getClienteInfo360: async (nroCliente: number): Promise<Info360Indicadores> => {
        const response = await api.get<Info360Indicadores>(`/api/Clientes/${nroCliente}/info-360`);
        const data = response.data;
        return {
            ...data,
            chequesDevueltos12Meses: data.chequesDevueltos12Meses ?? {
                cantidad: 0,
                importeTotal: 0,
                items: [],
            },
        };
    },

    createCliente: async (cliente: any): Promise<Cliente> => {
        const response = await api.post<Cliente>('/api/Clientes', cliente);
        return response.data;
    },

    createClientesBulk: async (clientes: any[]): Promise<{ exitosos: number; fallidos: number; errores: { indice: number; nroCliente: number; mensaje: string }[] }> => {
        const response = await api.post<{ exitosos: number; fallidos: number; errores: { indice: number; nroCliente: number; mensaje: string }[] }>('/api/Clientes/Bulk', clientes);
        return response.data;
    },

    updateCliente: async (nroCliente: number, cliente: Cliente): Promise<void> => {
        await api.put(`/api/Clientes/${nroCliente}`, cliente);
    },

    puedeEliminar: async (nroCliente: number) => {
        const response = await api.get<{ canDelete: boolean; motivos?: string[]; message?: string }>(
            `/api/Clientes/${nroCliente}/puede-eliminar`
        );
        return response.data;
    },

    deleteCliente: async (nroCliente: number): Promise<void> => {
        await api.delete(`/api/Clientes/${nroCliente}`);
    },

    getProximoNumero: async (): Promise<number> => {
        const response = await api.get<number>('/api/Clientes/ProximoNumero');
        return response.data;
    },

    getNotasOperativasComerciales: async (nroCliente: number): Promise<Info360Nota[]> => {
        const response = await api.get<Info360Nota[]>(`/api/Clientes/${nroCliente}/notas`);
        return response.data;
    },

    getPadronAfip: async (cuit: string, idWsfe?: string): Promise<PadronAfipCliente> => {
        const cuitPath = normalizeCuitForStorage(cuit);
        const response = await api.get<PadronAfipCliente>(
            `/api/Clientes/padron-afip/${cuitPath}`,
            { params: idWsfe ? { idWsfe } : undefined }
        );
        return response.data;
    },
};

export default clienteService; 